import React, { useEffect } from 'react';
import { useNotification, Notification as AppNotification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import '../components/Notification/NotificationBell.css';

const NotificationPage: React.FC = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotification();
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    const formatTimeAgo = (date: Date) => {
        const now = new Date();
        // Force both to UTC for accurate difference
        const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
        const utcDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
        
        const diffInSeconds = Math.floor((utcNow - utcDate) / 1000);
        
        if (diffInSeconds < 60) return language === 'vi' ? 'Vừa xong' : 'Just now';
        
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        if (diffInMinutes < 60) return language === 'vi' ? `${diffInMinutes} phút trước` : `${diffInMinutes} mins ago`;
        
        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return language === 'vi' ? `${diffInHours} giờ trước` : `${diffInHours} hours ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return language === 'vi' ? 'Hôm qua' : 'Yesterday';
        if (diffInDays < 7) return language === 'vi' ? `${diffInDays} ngày trước` : `${diffInDays} days ago`;
        
        return date.toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US');
    };

    const handleNotificationClick = (notification: AppNotification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.bookingId) {
            navigate(`/bookings?id=${notification.bookingId}`);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '1.5rem', margin: 0 }}>
                    🔔 {t('notifications.title')}
                    {unreadCount > 0 && <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.9rem', marginLeft: '10px' }}>{unreadCount}</span>}
                </h1>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            style={{ padding: '8px 12px', background: 'transparent', border: '1px solid #667eea', color: '#667eea', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
                        >
                            {t('notifications.markAllRead')}
                        </button>
                    )}
                    {notifications.length > 0 && (
                        <button
                            onClick={clearNotifications}
                            style={{ padding: '8px 12px', background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
                        >
                            🗑️ {t('notifications.clearAll')}
                        </button>
                    )}
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                {notifications.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280' }}>
                        <span style={{ fontSize: '3rem', display: 'block', marginBottom: '10px' }}>📭</span>
                        <p style={{ fontSize: '1.1rem' }}>{t('notifications.empty')}</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {notifications.map((notification) => {
                            let displayTitle = notification.title;
                            let displayMsg = notification.message;
                            if (language === 'en') {
                                displayTitle = displayTitle
                                    .replace('Có lịch đặt mới!', 'New online booking!')
                                    .replace('Báo Cáo Tổng Doanh Thu Tiệm', 'Daily Revenue Report')
                                    .replace('Báo Cáo Thu Nhập Cuối Ngày', 'End of Day Earnings Report')
                                    .replace('Khách hàng sắp tới', 'Upcoming Appointment');
                                
                                displayMsg = displayMsg
                                    .replace('Khách hàng:', 'Customer:')
                                    .replace('SĐT:', 'Phone:')
                                    .replace('Dịch vụ:', 'Service:')
                                    .replace('Giờ:', 'Time:')
                                    .replace('Hôm nay tiệm của bạn có tổng doanh thu là:', 'Today your business total revenue is:')
                                    .replace('. Xem chi tiết ở trang Thu Nhập.', '. Check the Earnings page for details.')
                                    .replace('Hôm nay bạn đã phục vụ', 'Today you served')
                                    .replace('dịch vụ với tổng doanh thu (gồm TIP) là:', 'services with total earnings (including TIP):')
                                    .replace('. Chúc mừng bạn một ngày làm việc tuyệt vời!', '. Congratulations on a great workday!')
                                    .replace('Khách hàng tiếp theo sẽ tới trong ~10p nữa', 'Your next client will arrive in ~10 mins')
                                    .replace('. Hãy chú ý thời gian.', '. Please keep an eye on the time.');
                            }

                            return (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                        padding: '16px 20px',
                                        borderBottom: '1px solid #f3f4f6',
                                        backgroundColor: notification.read ? 'white' : '#f0fdf4',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!notification.read) e.currentTarget.style.backgroundColor = '#dcfce7';
                                        else e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!notification.read) e.currentTarget.style.backgroundColor = '#f0fdf4';
                                        else e.currentTarget.style.backgroundColor = 'white';
                                    }}
                                >
                                    <div style={{ fontSize: '1.5rem', marginRight: '15px' }}>
                                        {notification.type === 'booking' ? '📅' : 'ℹ️'}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: '#1f2937' }}>{displayTitle}</h4>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#4b5563', whiteSpace: 'pre-line', lineHeight: '1.4' }}>{displayMsg}</p>
                                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{formatTimeAgo(notification.timestamp)}</span>
                                    </div>
                                    {!notification.read && (
                                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981', alignSelf: 'center', marginLeft: '10px' }} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationPage;
